import React, { Fragment, useMemo } from "react";
import { useLocation, useSearchParams, Link } from "wouter";

import { createReactTableColumnHelper, ReactTableColumnDef } from "../../../components/src/react-table/features";

import { isValidMongoDBObjectID } from "../../../common/src/utils";
import { twoDecPriceFormat } from "../../../common/src/utils/FormatFunctions";
import { miniTransactionTypes } from "../containers/MiniTransactionForm";
import { ICommandBarMenuItem } from "../../../components/src/common/CommandBar";
import { ReactDataGrid } from "../../../components/src/ReactDataGrid";
import { getRandomFileName } from "../../../components/src/common/exportToXlsx2";
import { TransactionStatus } from "../types.generated";
import { TooltipLabel } from "../../../components/src/TooltipLabel";
export interface ITransactionDataRow {
    _id: string;
    clientId: string;
    clientName: string;
    brokerId: string;
    brokerName: string;
    amount: number;
    balance: number;
    currency: string;
    instrumentId: string;
    instrumentName: string;
    tradeDate: string;
    valueDate: string;
    type: string;
    description: string;
    status: string;
    error: string;
    reference: string; //TODO: for ba transactions, maybe create separate grid eventually..
    updatedByUserName: string;
    transactionItemId: string;
}

interface ITransactionsGrid {
    items: any[];
    commands?: ICommandBarMenuItem[];
    visibleColumns?: string[];
    onSelectedChange?(items: any[]): any;
    showExcelButton?: boolean;
    highlightRowOnClick?: boolean;
    linkDescriptionToBankAccountTransaction?: boolean;
}

// control visibility and the order of columns
const defaultVisibleColumns = [
    "clientName",
    "brokerName",
    "amount",
    "currency",
    "instrumentName",
    "tradeDate",
    "valueDate",
    "type",
    "description",
    "status",
    "_id",
    "updatedByUserName"
];

export const TransactionsGrid = ({
    items,
    commands,
    visibleColumns,
    onSelectedChange,
    showExcelButton = true,
    highlightRowOnClick = false,
    linkDescriptionToBankAccountTransaction = false
}: ITransactionsGrid): React.ReactElement => {
    const [location] = useLocation();
    const [queryArgs] = useSearchParams();

    const onSelectionChange = (items: any[]) => {
        if (onSelectedChange) {
            onSelectedChange(items);
        }
    };

    const allColumns = useMemo<ReactTableColumnDef<ITransactionDataRow>[]>(() => {
        const relativeLocation = (id: string): string => {
            const pathname = location.split("?")[0];
            const parts: string[] = pathname.split("/");
            const last = parts[parts.length - 1];
            if (isValidMongoDBObjectID(last)) {
                const path = pathname.replace(last, "");
                if (queryArgs.toString()) {
                    return `${path}${id}?${queryArgs.toString()}`;
                } else {
                    return `${path}${id}}`;
                }
            } else {
                const path = pathname.endsWith("/") ? pathname : `${pathname}/`;
                if (queryArgs.toString()) {
                    return `${path}${id}?${queryArgs.toString()}`;
                } else {
                    return `${path}${id}}`;
                }
            }
        };
        const columnHelper = createReactTableColumnHelper<ITransactionDataRow>();
        const allColumns = [
            columnHelper.accessor("clientName", {
                header: "Client",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return (
                        <Link key={value} to={`/parties/${cellProps.row.original.clientId}`}>
                            {value}
                        </Link>
                    );
                },
                size: 90
            }),
            columnHelper.accessor("brokerName", {
                header: "Broker",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return (
                        <Link key={value} to={`/parties/${cellProps.row.original.brokerId}`}>
                            {value}
                        </Link>
                    );
                },
                size: 90
            }),
            columnHelper.accessor("amount", {
                header: "Amount",
                cell: (cellProps) => {
                    return <div style={{ textAlign: "right" }}>{twoDecPriceFormat(cellProps.getValue())}</div>;
                }
            }),
            columnHelper.accessor("currency", {
                header: "Currency",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return <div>{value}</div>;
                }
            }),
            columnHelper.accessor("instrumentName", {
                header: "Instrument",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return (
                        <Link key={value} to={`/instruments/${cellProps.row.original.instrumentId}`}>
                            {value}
                        </Link>
                    );
                }
            }),
            columnHelper.accessor("tradeDate", {
                header: "Trade date",
                size: 120,
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return <Link to={relativeLocation(cellProps.row.original._id)}>{value}</Link>;
                }
            }),
            columnHelper.accessor("valueDate", {
                header: "Value date",
                size: 50,
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    if (value < new Date().toISOString().slice(0, 10) && cellProps.row.original.status !== TransactionStatus.Settled) {
                        return miniTransactionTypes.find((d) => d === cellProps.row.original.type) ? (
                            <Link to={relativeLocation(cellProps.row.original._id)}>
                                {
                                    <span
                                        style={{
                                            color: "white",
                                            backgroundColor: "crimson"
                                        }}
                                    >
                                        {value}
                                    </span>
                                }
                            </Link>
                        ) : (
                            <span
                                style={{
                                    color: "white",
                                    backgroundColor: "crimson"
                                }}
                            >
                                {value}
                            </span>
                        );
                    } else {
                        return miniTransactionTypes.find((d) => d === cellProps.row.original.type) ? (
                            <Link to={relativeLocation(cellProps.row.original._id)}>{value}</Link>
                        ) : (
                            <span>{value}</span>
                        );
                    }
                }
            }),
            columnHelper.accessor("type", {
                header: "Type",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return miniTransactionTypes.find((d) => d === value) ? (
                        <Link to={relativeLocation(cellProps.row.original._id)}>{value}</Link>
                    ) : (
                        <span>{value}</span>
                    );
                }
            }),
            columnHelper.accessor("description", {
                header: "Description",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    if (linkDescriptionToBankAccountTransaction) {
                        return <Link to={`/bankaccounttransactions/${cellProps.row.original._id}`}>{value}</Link>;
                    }
                    return <div>{value}</div>;
                }
            }),
            columnHelper.accessor("reference", {
                header: "Reference",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return <TooltipLabel text={value} content={value} />;
                },
                size: 120
            }),
            columnHelper.accessor("status", {
                header: "Status",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return <div>{value}</div>;
                }
            }),
            columnHelper.accessor("_id", {
                header: "Id",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return (
                        <Link key={value} to={`/transaction/${value}`}>
                            {value}
                        </Link>
                    );
                }
            }),
            columnHelper.accessor("error", {
                header: "Error",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return <TooltipLabel text={value} content={value} />;
                }
            }),
            columnHelper.accessor("updatedByUserName", {
                header: "Updated by",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return <div>{value}</div>;
                }
            }),
            columnHelper.accessor("balance", {
                header: "Balance",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return (
                        <span
                            style={{
                                display: "block",
                                textAlign: "right"
                            }}
                        >
                            {twoDecPriceFormat(value)}
                        </span>
                    );
                }
            }),
            columnHelper.accessor("transactionItemId", {
                header: "Transaction item id",
                cell: (cellProps) => {
                    const value = cellProps.getValue();
                    return <div>{value}</div>;
                }
            })
        ];
        return allColumns;
    }, [location, queryArgs, linkDescriptionToBankAccountTransaction]);

    const visibleColumnKeys: string[] = visibleColumns ? visibleColumns : defaultVisibleColumns;
    // we want to keep the order of visible columns as well
    const columns = visibleColumnKeys.map((c) => {
        return allColumns.find((x: any) => x.accessorKey == c);
    });

    const selectedItemsCommands = (items: any[]): ICommandBarMenuItem[] => {
        if (items.length === 0) {
            return [];
        }
        return [
            {
                itemKey: "selectedTotalReconcile",
                text: () => {
                    const uniqueCurrencies = items.map((t: any) => t.currency).filter((item, i, arr) => arr.indexOf(item) === i);

                    if (uniqueCurrencies.length > 1) {
                        return `Multiple currencies ${uniqueCurrencies.join(", ")}`;
                    }

                    const amountValues = items.map((t: any) => t.amount);

                    return `${twoDecPriceFormat(amountValues.reduce((sum: number, current: number) => sum + current, 0))}`;
                },
                className: () => {
                    const uniqueCurrencies = items.map((t: any) => t.currency).filter((item, i, arr) => arr.indexOf(item) === i);
                    return uniqueCurrencies.length > 1 ? "bg-red-500" : "";
                },
                title: "Total selected"
            }
        ];
    };

    return items && items.length > 0 ? (
        <div>
            <ReactDataGrid
                columns={columns}
                data={items}
                commands={commands}
                defaultHiddenColumns={null}
                exportToXlsxFile={showExcelButton ? getRandomFileName() : null}
                onSelectedChange={onSelectionChange}
                selectedItemsCommands={selectedItemsCommands}
                highlightRowOnClick={highlightRowOnClick}
            />
        </div>
    ) : (
        <Fragment>No data to display.</Fragment>
    );
};
